import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import PhotoUpload from './PhotoUpload';
import { useSiteData } from '@/context/SiteDataContext';

export default function SettingsEditor() {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const { refresh } = useSiteData();
  useEffect(() => {
    api.get('/site/settings').then(r => setForm(r.data)).catch(() => toast.error('Load failed'));
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch('/site/settings', form);
      toast.success('Settings saved — changes are live');
      refresh?.(['settings']);
    }
    catch { toast.error('Save failed'); } finally { setBusy(false); }
  };
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="max-w-4xl space-y-8">
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl text-royal font-bold">Site Settings</h3>
        <button onClick={save} disabled={busy} data-testid="settings-save-btn" className="btn-royal">{busy ? 'Saving...' : 'Save Settings'}</button>
      </div>

      <Section title="Hero (Homepage top banner)">
        <PhotoUpload label="Hero background image (fallback if no video)" value={form.hero_image} onChange={(v) => set('hero_image', v)} category="hero" />
        <Field label="Hero background VIDEO (mp4 URL) — overrides image if set">
          <input value={form.hero_video || ''} onChange={(e) => set('hero_video', e.target.value)} placeholder="https://.../video.mp4" className="w-full px-3 py-2 border rounded" data-testid="hero-video-input" />
        </Field>
        <PhotoUpload label="Upload new hero video (mp4)" value={form.hero_video} onChange={(v) => set('hero_video', v)} category="hero" accept="video/*" />
        <Two>
          <Field label="Hero title (main line)"><input value={form.hero_title_main || ''} onChange={(e) => set('hero_title_main', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
          <Field label="Hero title (accent, gold)"><input value={form.hero_title_accent || ''} onChange={(e) => set('hero_title_accent', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
        </Two>
        <Field label="Tagline"><textarea rows="2" value={form.tagline || ''} onChange={(e) => set('tagline', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
        <Field label="Hero overlay (CSS color / gradient — dims the video for readability)">
          <div className="flex gap-2 items-center">
            <input value={form.hero_overlay_color || ''} onChange={(e) => set('hero_overlay_color', e.target.value)}
              placeholder="rgba(31,15,53,0.6) or linear-gradient(...)"
              className="flex-1 px-3 py-2 border rounded font-mono text-xs" />
            <div className="w-24 h-10 rounded border" style={{ background: form.hero_overlay_color || 'linear-gradient(to bottom, rgba(31,15,53,0.6), rgba(31,15,53,0.3), rgba(31,15,53,0.7))' }} />
          </div>
          <div className="text-[11px] text-charcoal-muted mt-1">Tip: darker overlay → whiter text stands out more. Try <code>rgba(31,15,53,0.75)</code>.</div>
        </Field>
        <div className="grid md:grid-cols-3 gap-3">
          {[1,2,3].map(n => (
            <div key={n} className="bg-royal/5 border rounded p-3">
              <div className="text-xs font-bold text-royal mb-2">CTA Button {n}</div>
              <input value={form[`hero_cta${n}_label`] || ''} onChange={(e) => set(`hero_cta${n}_label`, e.target.value)}
                placeholder={['Learn More', 'Upcoming Events', 'Watch Live'][n-1]}
                data-testid={`hero-cta${n}-label`}
                className="w-full px-3 py-2 border rounded text-sm mb-2" />
              <input value={form[`hero_cta${n}_href`] || ''} onChange={(e) => set(`hero_cta${n}_href`, e.target.value)}
                placeholder={['/about', '/events', '/media'][n-1] + ' or https://…'}
                data-testid={`hero-cta${n}-href`}
                className="w-full px-3 py-2 border rounded text-xs font-mono" />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Give Button (Header & Footer)">
        <Two>
          <Field label="Button label (shown in top-right + footer)">
            <input value={form.give_label || ''} onChange={(e) => set('give_label', e.target.value)} placeholder="Give" data-testid="give-label-input" className="w-full px-3 py-2 border rounded" />
          </Field>
          <Field label="Button destination (path or URL)">
            <input value={form.give_href || ''} onChange={(e) => set('give_href', e.target.value)} placeholder="/giving or https://givelify.com/…" data-testid="give-href-input" className="w-full px-3 py-2 border rounded font-mono text-xs" />
          </Field>
        </Two>
        <div className="text-xs text-charcoal-muted">A URL starting with <code>http</code> opens in a new tab. Anything else routes inside the site (e.g. <code>/giving</code>).</div>
      </Section>

      <Section title="Footer Appearance">
        <Field label="Footer background color">
          <div className="flex gap-2">
            <input type="color" value={form.footer_bg_color || '#1F0F35'} onChange={(e) => set('footer_bg_color', e.target.value)} className="w-12 h-10 rounded border" data-testid="footer-bg-picker" />
            <input value={form.footer_bg_color || ''} onChange={(e) => set('footer_bg_color', e.target.value)} placeholder="#1F0F35" className="flex-1 px-3 py-2 border rounded font-mono text-xs" />
          </div>
        </Field>
        <Field label="Footer tagline (paragraph under the logo)">
          <textarea rows="2" value={form.footer_tagline || ''} onChange={(e) => set('footer_tagline', e.target.value)} className="w-full px-3 py-2 border rounded" />
        </Field>
      </Section>

      <Section title="Brand / Identity">
        <Two>
          <Field label="Site name"><input value={form.site_name || ''} onChange={(e) => set('site_name', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
          <Field label="Subtitle"><input value={form.site_subtitle || ''} onChange={(e) => set('site_subtitle', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
        </Two>
        <PhotoUpload label="Logo" value={form.logo_url} onChange={(v) => set('logo_url', v)} category="brand" />
      </Section>

      <Section title="Browser Tab / Favicon">
        <div className="text-xs text-charcoal-muted -mt-2 mb-2">
          The favicon is the tiny icon that appears in the browser tab, bookmarks, and phone home-screen shortcuts.
          Use a square PNG (at least 192×192 px). Changes save immediately and the new icon shows in the tab within a second.
        </div>
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex flex-col items-center gap-2">
            <div className="bg-white border rounded p-3 flex items-center justify-center" style={{ width: 88, height: 88 }}>
              {form.favicon
                ? <img data-testid="favicon-preview-lg" alt="favicon preview" src={form.favicon.startsWith('http') ? form.favicon : `${process.env.REACT_APP_BACKEND_URL}${form.favicon}`} className="max-w-full max-h-full object-contain" />
                : <span className="text-[10px] text-charcoal-muted">No favicon set</span>}
            </div>
            <span className="text-[10px] text-charcoal-muted">Tab preview (64px)</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="bg-royal-dark rounded-t px-2 pt-1 flex items-center gap-1" style={{ minWidth: 160 }}>
              {form.favicon
                ? <img alt="tab" src={form.favicon.startsWith('http') ? form.favicon : `${process.env.REACT_APP_BACKEND_URL}${form.favicon}`} className="w-3.5 h-3.5 object-contain" />
                : <div className="w-3.5 h-3.5 bg-white/20 rounded-sm" />}
              <div className="text-[10px] text-white truncate">{form.site_name || 'Site'}</div>
            </div>
            <span className="text-[10px] text-charcoal-muted">In-tab preview</span>
          </div>
          <div className="flex-1 min-w-[240px]">
            <PhotoUpload label="Upload favicon (square PNG recommended)" value={form.favicon} onChange={(v) => set('favicon', v)} category="brand" accept="image/png,image/x-icon,image/webp,image/jpeg,image/svg+xml" />
            {form.favicon && (
              <button
                type="button"
                onClick={() => set('favicon', '')}
                data-testid="favicon-clear-btn"
                className="mt-2 text-xs text-red-600 hover:text-red-700 underline"
              >
                Remove favicon (revert to default)
              </button>
            )}
          </div>
        </div>
      </Section>

      <Section title="Theme (Colors & Fonts — applied site-wide)">
        <Two>
          <Field label="Primary color">
            <div className="flex gap-2">
              <input type="color" value={form.primary_color || '#3B1E5E'} onChange={(e) => set('primary_color', e.target.value)} className="w-12 h-10 rounded border" />
              <input value={form.primary_color || ''} onChange={(e) => set('primary_color', e.target.value)} className="flex-1 px-3 py-2 border rounded" />
            </div>
          </Field>
          <Field label="Primary dark shade">
            <div className="flex gap-2">
              <input type="color" value={form.primary_dark || '#1F0F35'} onChange={(e) => set('primary_dark', e.target.value)} className="w-12 h-10 rounded border" />
              <input value={form.primary_dark || ''} onChange={(e) => set('primary_dark', e.target.value)} className="flex-1 px-3 py-2 border rounded" />
            </div>
          </Field>
          <Field label="Accent color (gold)">
            <div className="flex gap-2">
              <input type="color" value={form.accent_color || '#C9A227'} onChange={(e) => set('accent_color', e.target.value)} className="w-12 h-10 rounded border" />
              <input value={form.accent_color || ''} onChange={(e) => set('accent_color', e.target.value)} className="flex-1 px-3 py-2 border rounded" />
            </div>
          </Field>
        </Two>
        <Two>
          <Field label="Heading font (Google Fonts name)">
            <select value={form.heading_font || 'Playfair Display'} onChange={(e) => set('heading_font', e.target.value)} className="w-full px-3 py-2 border rounded">
              {['Playfair Display', 'Cinzel', 'Cormorant Garamond', 'Libre Baskerville', 'Merriweather', 'DM Serif Display', 'Lora', 'Space Grotesk', 'Montserrat'].map(f => <option key={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Body font">
            <select value={form.body_font || 'Poppins'} onChange={(e) => set('body_font', e.target.value)} className="w-full px-3 py-2 border rounded">
              {['Poppins', 'Inter', 'Roboto', 'Open Sans', 'Nunito Sans', 'Work Sans', 'DM Sans', 'Lato'].map(f => <option key={f}>{f}</option>)}
            </select>
          </Field>
        </Two>
      </Section>

      <Section title="Statistics (Homepage counter)">
        <Two>
          <Field label="Local Churches"><input value={form.stat_churches || ''} onChange={(e) => set('stat_churches', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
          <Field label="Departments"><input value={form.stat_departments || ''} onChange={(e) => set('stat_departments', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
          <Field label="Years Serving"><input value={form.stat_years || ''} onChange={(e) => set('stat_years', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
        </Two>
      </Section>

      <Section title="Mission / Vision / Values">
        <Field label="Mission"><textarea rows="3" value={form.mission || ''} onChange={(e) => set('mission', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
        <Field label="Vision"><textarea rows="3" value={form.vision || ''} onChange={(e) => set('vision', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
        <Field label="Core Values"><textarea rows="2" value={form.core_values || ''} onChange={(e) => set('core_values', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
      </Section>

      <Section title="Contact & Location">
        <Two>
          <Field label="Address line 1"><input value={form.address_line1 || ''} onChange={(e) => set('address_line1', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
          <Field label="Address line 2"><input value={form.address_line2 || ''} onChange={(e) => set('address_line2', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
          <Field label="Phone"><input value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
          <Field label="Email"><input value={form.email || ''} onChange={(e) => set('email', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
          <Field label="Office hours"><input value={form.office_hours || ''} onChange={(e) => set('office_hours', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
        </Two>
      </Section>

      <Section title="Integrations & Social">
        <Field label="YouTube Live embed URL"><input value={form.youtube_live_url || ''} onChange={(e) => set('youtube_live_url', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
        <Field label="Givelify donation URL"><input value={form.givelify_url || ''} onChange={(e) => set('givelify_url', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
        <Field label="Facebook Page URL (used by the “Follow us on Facebook” link on the News page)">
          <input value={form.facebook_page_url || ''} onChange={(e) => set('facebook_page_url', e.target.value)} data-testid="facebook-page-url" placeholder="https://www.facebook.com/YourPage/" className="w-full px-3 py-2 border rounded" />
        </Field>
        <Two>
          <Field label="Facebook URL"><input value={form.facebook_url || ''} onChange={(e) => set('facebook_url', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
          <Field label="Instagram URL"><input value={form.instagram_url || ''} onChange={(e) => set('instagram_url', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
          <Field label="YouTube URL"><input value={form.youtube_url || ''} onChange={(e) => set('youtube_url', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
          <Field label="Twitter URL"><input value={form.twitter_url || ''} onChange={(e) => set('twitter_url', e.target.value)} className="w-full px-3 py-2 border rounded" /></Field>
        </Two>
      </Section>

      <div><button onClick={save} disabled={busy} className="btn-royal">{busy ? 'Saving...' : 'Save Settings'}</button></div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border rounded-lg p-6">
      <h4 className="font-serif text-lg text-royal font-bold mb-4">{title}</h4>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (<div><div className="text-sm font-semibold text-royal mb-1">{label}</div>{children}</div>);
}
function Two({ children }) {
  return <div className="grid md:grid-cols-2 gap-4">{children}</div>;
}
